import { getOrderById } from 'api/orderLookup'
import {
  ITEM_STATUS_CANCELLED,
  ITEM_STATUS_COMPLETED,
  ITEM_STATUS_FAILED,
  ITEM_STATUS_PROCESSING_ADDON,
} from 'constants/cart'
import OrderError from 'lib/../errors/orderError'
import pollOrderService from 'lib/polling/pollOrderService'

export const FLIGHT_ITEM_NOT_COMPLETED_ERROR = 'Can only add addons to completed flight items'
export const FLIGHT_ITEM_ADDON_PROCESSING_ERROR = 'Flight item is already processing an addon'

const FLIGHT_ITEM_READY_POLL_MS = 105000
const FLIGHT_ITEM_READY_POLL_INTERVAL_MS = 300

export function isFlightItemNotCompletedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const body = error as { status?: number, errors?: Array<unknown> }
  return body.status === 400 && Array.isArray(body.errors) && body.errors.includes(FLIGHT_ITEM_NOT_COMPLETED_ERROR)
}

/**
 * svc-order rejects addon POSTs unless the parent flight item is completed.
 * After a booking or a previous addon wave the item can sit in created/pending
 * for a moment; this waits until it is safe to POST.
 */
export async function waitForCompletedFlightItem(
  orderId: string,
  flightItemId: string,
): Promise<App.Order> {
  return pollOrderService({
    validateFunction: (order: App.Order) => {
      const flightItem = order.flightItems.find(item => item.id === flightItemId)
      if (!flightItem) return undefined

      if (
        flightItem.status === ITEM_STATUS_CANCELLED
        || flightItem.status === ITEM_STATUS_FAILED
      ) {
        throw new OrderError(FLIGHT_ITEM_NOT_COMPLETED_ERROR)
      }

      // Refreshing mid-checkout leaves the item here. Waiting for completed would
      // hang; fail so the customer can retry from a completed booking.
      if (flightItem.status === ITEM_STATUS_PROCESSING_ADDON) {
        throw new OrderError(FLIGHT_ITEM_ADDON_PROCESSING_ERROR)
      }

      return flightItem.status === ITEM_STATUS_COMPLETED ? order : undefined
    },
    failedStatus: 'failed',
    apiCall: () => getOrderById(orderId),
    maxTime: FLIGHT_ITEM_READY_POLL_MS,
    waitTime: FLIGHT_ITEM_READY_POLL_INTERVAL_MS,
  })
}
